"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

/* ---- types ---- */

type ToastVariant = "success" | "error" | "info" | "warning";

interface ToastItem {
  id: string;
  variant: ToastVariant;
  message: string;
  durationMs: number;
}

interface ToastContextValue {
  toast: (
    _variant: ToastVariant,
    _message: string,
    _durationMs?: number
  ) => void;
  success: (_message: string, _durationMs?: number) => void;
  error: (_message: string, _durationMs?: number) => void;
  info: (_message: string, _durationMs?: number) => void;
  warning: (_message: string, _durationMs?: number) => void;
}

/* ---- context ---- */

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

/* ---- style maps ---- */

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  success:
    "border-[var(--v2-success-border,#bbf7d0)] bg-[var(--v2-success-bg,#f0fdf4)] text-[var(--v2-success-text,#166534)]",
  error: "border-red-200 bg-red-50 text-red-700",
  info: "border-blue-200 bg-blue-50 text-blue-700",
  warning: "border-amber-200 bg-amber-50 text-amber-700",
};

const VARIANT_ICONS: Record<ToastVariant, string> = {
  success: "✓",
  error: "✕",
  info: "ℹ",
  warning: "⚠",
};

const DEFAULT_DURATION = 4000;

/* ---- provider ---- */

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const counterRef = React.useRef(0);

  const remove = React.useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = React.useCallback(
    (variant: ToastVariant, message: string, durationMs = DEFAULT_DURATION) => {
      counterRef.current += 1;
      const id = `toast-${String(counterRef.current)}`;
      const item: ToastItem = { id, variant, message, durationMs };
      setItems((prev) => [...prev, item]);
    },
    []
  );

  const api = React.useMemo<ToastContextValue>(
    () => ({
      toast: addToast,
      success: (m, d) => addToast("success", m, d),
      error: (m, d) => addToast("error", m, d),
      info: (m, d) => addToast("info", m, d),
      warning: (m, d) => addToast("warning", m, d),
    }),
    [addToast]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* Toast container */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed right-4 top-4 z-[200] flex w-full max-w-sm flex-col gap-2"
      >
        {items.map((item) => (
          <ToastCard key={item.id} item={item} onDismiss={remove} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/* ---- single toast card ---- */

function ToastCard({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (_id: string) => void;
}) {
  React.useEffect(() => {
    if (item.durationMs <= 0) return;
    const timer = setTimeout(() => onDismiss(item.id), item.durationMs);
    return () => clearTimeout(timer);
  }, [item.id, item.durationMs, onDismiss]);

  return (
    <div
      role={item.variant === "error" ? "alert" : "status"}
      aria-live={item.variant === "error" ? "assertive" : "polite"}
      className={cn(
        "pointer-events-auto flex items-start gap-3 rounded-[var(--v2-radius-md)] border px-4 py-3 text-sm shadow-lg animate-in slide-in-from-right-5 fade-in duration-200",
        VARIANT_CLASSES[item.variant]
      )}
    >
      <span className="mt-0.5 text-base leading-none" aria-hidden="true">
        {VARIANT_ICONS[item.variant]}
      </span>
      <span className="flex-1 leading-relaxed">{item.message}</span>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        aria-label="Bildirimi kapat"
        className="v2-focus-ring -mr-1 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-current opacity-60 transition hover:opacity-100"
      >
        ✕
      </button>
    </div>
  );
}
